import { ICellRendererParams } from "ag-grid-community";
import { Blocks } from "lucide-react";
import React, { useMemo } from "react";

import { IgPaper, IgTooltip } from "@intelligems/ig-theme";
import {
  ExperienceListDto,
  MessageBoxWidgetDto,
  OrganizationDto,
  ProgressBarWidgetDto,
  QuantityButtonsWidgetDto,
  ScheduleDto,
  UserInterfaceDto,
} from "@intelligems/ig-types";
import { createProgressBarConfigForOffer, merge } from "@intelligems/ig-utils";
import { resolveProgressBarWidgetConfig } from "@intelligems/ig-widgets/ProgressBarView/resolveProgressBarWidgetConfig";
import { Box, CircularProgress, Grid, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useGetScriptStatus } from "@src/api/shops/get-script-status";
import {
  generateProgressBarWidgetForPreview,
  generateQuantityButtonWidgetForPreview,
} from "@src/components/ColorAndStyle/utils/utils";
import { IgMessageBox } from "@src/domains/WidgetLibrary/MessageBox/IgMessageBox";
import { IgProgressBar } from "@src/domains/WidgetLibrary/ProgressBar/IgProgressBar";
import { IgQuantityButtons } from "@src/domains/WidgetLibrary/QuantityButtons/IgQuantityButtons";
import { MoreMenuPersonalization } from "@src/features/personalizations/components/MoreMenuPersonalization";
import { ProgressBarContextProvider } from "@src/features/personalizations/components/offerModifications/userInterfaces/progressBar/ProgressBarContextProvider";
import { isUsedInExperiment } from "@src/features/personalizations/utils";
import { useIsExperienceMutating } from "@src/hooks/use-is-experience-mutating";
import { isMessageBox, isProgressBar } from "@src/store/slices/widgets/util";

export const StyledCompactBox = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [`& .ig-volume-widget-container`]: {
    width: "100% !important",
  },
}));

export const StyledBaseBox = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [`& .ig-volume-widget-button`]: {
    padding: "11px 7px",
  },
  [`& .ig-quantity-image`]: {
    width: "15.78px !important",
  },
  [`& .ig-quantity-widget-badge`]: {
    height: "10px",

    padding: "2.75px 4.4px",
    borderBottomLeftRadius: "2px",
    borderBottomRightRadius: "2px",
  },
  [`& .ig-message-box-container`]: {
    minHeight: "45px !important",
    width: "100% !important",
  },
  [`& .ig-quantity-radio-button`]: {
    width: "8px !important",
    height: "8px !important",
    "&::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "4px",
      height: "4px",
    },
    [`& .ig-quantity-image-placeholder`]: {
      width: "25px !important",
      height: "25px !important",
      maxWidth: "20px !important",
      minWidth: "20px !important",
      minHeight: "20px !important",
      maxHeight: "20px !important",
      "& .lucide-image": { width: "11px !important" },
    },
    [`& .ig-quantity-free-gift-image`]: {
      width: "30px",
      height: "30px",
      maxWidth: "30px !important",
      minWidth: "30px !important",
      maxHeight: "30px",
      "& .lucide-image": { width: "11px !important" },
    },
  },
}));

interface MoreMenuCellRendererProps extends ICellRendererParams {
  experiments: ExperienceListDto[];
  org: OrganizationDto;
  schedules: ScheduleDto[];
  schedulesLoading: boolean;
}

export const MoreMenuCellRenderer = (props: MoreMenuCellRendererProps) => {
  const { experiments, org, schedules, schedulesLoading, data } = props;

  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => schedule.experienceId === data.id);
  }, [schedules]);

  const scriptStatus = useGetScriptStatus(org.id);
  const isUsed = isUsedInExperiment(experiments, data.id);

  const isArchiveMutating = useIsExperienceMutating(
    "archiveExperience",
    data.id
  );
  const isUnarchiveMutating = useIsExperienceMutating(
    "unarchiveExperience",
    data.id
  );
  const isDuplicateMutating = useIsExperienceMutating(
    "duplicateExperience",
    data.id
  );
  const isDeleteMutating = useIsExperienceMutating("deleteExperience", data.id);

  if (scriptStatus.isLoading || !scriptStatus.data) {
    return null;
  }

  if (
    isArchiveMutating === 1 ||
    isUnarchiveMutating === 1 ||
    isDuplicateMutating === 1 ||
    isDeleteMutating === 1
  ) {
    return <CircularProgress size={24} />;
  }

  return (
    <MoreMenuPersonalization
      isUsedInTest={isUsed}
      experience={data}
      variations={data.variations}
      organization={org}
      renderLocation={"overview"}
      schedules={filteredSchedules}
      scriptStatus={scriptStatus.data}
      isSchedulesLoading={schedulesLoading}
    />
  );
};

export const ComponentsCellRenderer = (params: ICellRendererParams) => {
  const userInterfaces: UserInterfaceDto[] =
    params.data.variations[0].userInterfaces || [];
  const theme = useTheme();
  let hasMessageBox = false;
  let messageBox: MessageBoxWidgetDto | null = null;
  let hasQuantityButtons = false;
  let quantityButtonsWidget: QuantityButtonsWidgetDto | null = null;
  let hasProgressbar = false;
  let progressBarWidget: ProgressBarWidgetDto | null = null;

  userInterfaces.forEach((userInterface: UserInterfaceDto) => {
    if (userInterface.isEnabled && userInterface.widget) {
      const desktopStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.desktop?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.desktop?.styles;

      const mobileStyles = userInterface.styleOverrides
        ? merge(
            userInterface.widget.config?.mobile?.styles || {},
            userInterface.styleOverrides
          )
        : userInterface.widget.config?.mobile?.styles;

      const widget = merge(userInterface.widget, {
        config: {
          desktop: {
            variables: userInterface.desktopVariables,
            styles: desktopStyles,
          },
          mobile: {
            variables: userInterface.mobileVariables,
            styles: mobileStyles,
          },
        },
      });

      if (isMessageBox(userInterface.widget)) {
        hasMessageBox = true;
        messageBox = widget as MessageBoxWidgetDto;
      } else if (isProgressBar(userInterface.widget)) {
        hasProgressbar = true;
        progressBarWidget = widget as ProgressBarWidgetDto;
      } else if (userInterface.widget?.widgetType === "quantityButtons") {
        hasQuantityButtons = true;
        quantityButtonsWidget = widget as QuantityButtonsWidgetDto;
      }
    }
  });

  const scaledProgressBarConfig = useMemo(() => {
    if (!progressBarWidget) return undefined;

    const scaledWidget = generateProgressBarWidgetForPreview(
      progressBarWidget,
      true
    );
    const widgetConfig = resolveProgressBarWidgetConfig(
      scaledWidget,
      params.data.variations[0].offer
    );
    return createProgressBarConfigForOffer(
      params.data.variations[0].offer,
      widgetConfig,
      true
    );
  }, [progressBarWidget]);

  if (!hasProgressbar && !hasQuantityButtons && !hasMessageBox) return null;

  return (
    <IgTooltip
      maxWidth={"none"}
      arrow={false}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: `${theme.igBackgroundColors.baseLightHigh} !important`,
            border: `1px solid ${theme.igBorders.core.default}`,
          },
        },
      }}
      title={
        <Grid sx={{ padding: 2 }}>
          <Grid
            container
            alignItems={"center"}
            justifyContent={"center"}
            wrap={"nowrap"}
            direction={"row"}
            columnSpacing={2}
          >
            {hasProgressbar && progressBarWidget && scaledProgressBarConfig && (
              <Grid item sx={{ width: "100%" }}>
                <IgPaper sx={{ padding: 8, height: "200px", width: "200px" }}>
                  <Grid
                    container
                    justifyContent={"center"}
                    alignItems={"center"}
                    height={"100%"}
                  >
                    <ProgressBarContextProvider
                      config={scaledProgressBarConfig}
                      mode={
                        (progressBarWidget as ProgressBarWidgetDto)
                          .widgetType === "shippingProgressBar"
                          ? "toggle"
                          : "input"
                      }
                    >
                      <IgProgressBar
                        device={"desktop"}
                        config={scaledProgressBarConfig}
                      />
                    </ProgressBarContextProvider>
                  </Grid>
                </IgPaper>
              </Grid>
            )}
            {hasMessageBox && messageBox && (
              <Grid item sx={{ width: "100%" }}>
                <IgPaper sx={{ padding: 8, height: "200px", width: "200px" }}>
                  <Grid
                    container
                    justifyContent={"center"}
                    alignItems={"center"}
                    height={"100%"}
                  >
                    <IgMessageBox device={"desktop"} widget={messageBox} />
                  </Grid>
                </IgPaper>
              </Grid>
            )}

            {hasQuantityButtons && quantityButtonsWidget && (
              <Grid item>
                <IgPaper sx={{ padding: 8 }}>
                  <Grid
                    container
                    justifyContent={"center"}
                    alignItems={"center"}
                    height={"100%"}
                  >
                    {(quantityButtonsWidget as QuantityButtonsWidgetDto)?.config
                      ?.desktop?.variant === "quantityButtonsWidgetCompact" ? (
                      <StyledCompactBox>
                        <IgQuantityButtons
                          gridSx={{ width: "250px" }}
                          widget={generateQuantityButtonWidgetForPreview(
                            quantityButtonsWidget,
                            true
                          )}
                          offer={params.data.variations[0].offer}
                          device={"desktop"}
                          focusedOfferVariant={undefined}
                        />
                      </StyledCompactBox>
                    ) : (
                      <StyledBaseBox>
                        <IgQuantityButtons
                          gridSx={{
                            width:
                              (
                                quantityButtonsWidget as QuantityButtonsWidgetDto
                              ).config?.desktop?.styles?.appearance?.layout ===
                              "row"
                                ? "340px"
                                : "210px",
                          }}
                          widget={generateQuantityButtonWidgetForPreview(
                            quantityButtonsWidget,
                            true
                          )}
                          offer={params.data.variations[0].offer}
                          device={"desktop"}
                          focusedOfferVariant={undefined}
                        />
                      </StyledBaseBox>
                    )}
                  </Grid>
                </IgPaper>
              </Grid>
            )}
          </Grid>
        </Grid>
      }
    >
      <Blocks size={16} />
    </IgTooltip>
  );
};
